import { Box, Button, FormLabel, Input, Stack, Textarea, Typography, CircularProgress } from '@mui/joy'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { CompanyInfoFormData, companyInfoValidationSchema } from '../types'
import FormField from './FormField'
import { useCarrierLookup } from '../../../../dashboard/pages/extension-signup/fmcsa/useCarrierLookup'
import { CarrierSuggestion } from '../../../../dashboard/pages/extension-signup/fmcsa/types'

interface CompanyInfoStepProps {
    onNext: (data: CompanyInfoFormData) => void
    initialData?: Partial<CompanyInfoFormData>
    submitLabel?: string
    // When set, mcConflict.applicationId === currentApplicationId is treated as a no-op
    // (the user did not actually change MC for this app). Settings/edit context.
    currentApplicationId?: string
}

export const CompanyInfoStep = ({ onNext, initialData = {}, submitLabel, currentApplicationId }: CompanyInfoStepProps) => {
    const [showMcSuggestions, setShowMcSuggestions] = useState(false)
    const {
        suggestions: mcSuggestions,
        isLoading: isMcLoading,
        mcConflict,
        search: searchMc,
    } = useCarrierLookup({
        debounceMs: 500,
    })

    // Treat conflict on the user's own current app as "no change" — in settings/edit
    // mode, typing the same MC the app already has is normal.
    const blockingMcConflict = mcConflict && mcConflict.applicationId !== currentApplicationId ? mcConflict : null

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        setError,
        clearErrors,
        watch,
    } = useForm<CompanyInfoFormData>({
        resolver: yupResolver(companyInfoValidationSchema),
        mode: 'onChange',
        defaultValues: initialData,
    })

    // Mirror the duplicate-MC conflict into the form's error state so the input
    // visually turns red AND react-hook-form's submit guard refuses to fire.
    // This is a HARD block — same MC must not be creatable in any code path.
    // A ref tracks whether we are the owner of the current mcNumber error so we
    // only clear errors we set ourselves (yup-driven errors stay intact).
    const setDuplicateErrorRef = useRef(false)
    useEffect(() => {
        if (blockingMcConflict) {
            setError('mcNumber', {
                type: 'duplicate',
                message: `MC already used by "${blockingMcConflict.companyName || blockingMcConflict.projectName}" in your organization`,
            })
            setDuplicateErrorRef.current = true
        } else if (setDuplicateErrorRef.current) {
            clearErrors('mcNumber')
            setDuplicateErrorRef.current = false
        }
    }, [blockingMcConflict, setError, clearErrors])

    // One MC docket can be held by MORE THAN ONE carrier in FMCSA's data (docket numbers get
    // reused/reassigned), which is why the dropdown can list two companies under the same MC.
    // The DOT is what tells them apart everywhere downstream — carrier verification sends its
    // code to the FMCSA contact of the DOT on file — so a company must never be created with an
    // ambiguous MC and no DOT: it would silently verify against whichever carrier FMCSA happens
    // to list first.
    const dotNumber = watch('dotNumber')
    const cleanDotNumber = (dotNumber || '').replace(/\D/g, '')
    const mcIsAmbiguous = mcSuggestions.length > 1 && !mcSuggestions.some((carrier) => String(carrier.dotNumber) === cleanDotNumber)

    // Only one carrier holds this MC — there is nothing to disambiguate, so fill its DOT in
    // rather than making the user pick from a single-item list.
    useEffect(() => {
        if (mcSuggestions.length === 1 && !cleanDotNumber && mcSuggestions[0].dotNumber) {
            setValue('dotNumber', String(mcSuggestions[0].dotNumber), { shouldValidate: true })
        }
    }, [mcSuggestions, cleanDotNumber, setValue])

    // Same owned-error bookkeeping as the duplicate-MC block above: only clear what we set.
    const setAmbiguousErrorRef = useRef(false)
    useEffect(() => {
        if (mcIsAmbiguous) {
            setError('dotNumber', {
                type: 'ambiguousMc',
                message: `MC ${mcSuggestions[0].mcNumber ?? ''} is registered to ${mcSuggestions.length} carriers — pick yours from the MC list so the right DOT is used`,
            })
            setAmbiguousErrorRef.current = true
        } else if (setAmbiguousErrorRef.current) {
            clearErrors('dotNumber')
            setAmbiguousErrorRef.current = false
        }
    }, [mcIsAmbiguous, mcSuggestions, setError, clearErrors])

    // Handle MC number change with lookup
    const handleMcNumberChange = useCallback(
        (value: string) => {
            const cleanValue = value.replace(/\D/g, '')
            setValue('mcNumber', cleanValue, { shouldValidate: true, shouldDirty: true })
            searchMc(cleanValue)
            setShowMcSuggestions(true)
        },
        [searchMc, setValue]
    )

    // Handle carrier selection from suggestions
    const handleSelectCarrier = useCallback(
        (carrier: CarrierSuggestion) => {
            // Hide the dropdown but DON'T clearMcSuggestions — that resets mcConflict to null and
            // would silently turn a duplicate-MC error green just because the user clicked the
            // matching suggestion. Re-trigger the search after we set the new MC value so conflict
            // state refreshes against the chosen MC.
            setShowMcSuggestions(false)

            // Auto-fill all fields from carrier data
            if (carrier.mcNumber) {
                const mcStr = String(carrier.mcNumber)
                setValue('mcNumber', mcStr, { shouldValidate: true })
                searchMc(mcStr)
            }
            if (carrier.dotNumber) setValue('dotNumber', String(carrier.dotNumber))
            if (carrier.legalName) setValue('companyName', carrier.legalName, { shouldValidate: true })
            if (carrier.dbaName) setValue('dba', carrier.dbaName)
            if (carrier.address?.street) setValue('address1', carrier.address.street, { shouldValidate: true })
            if (carrier.address?.city) setValue('city', carrier.address.city, { shouldValidate: true })
            if (carrier.address?.state) setValue('state', carrier.address.state, { shouldValidate: true })
            if (carrier.address?.zipCode) setValue('zipCode', carrier.address.zipCode, { shouldValidate: true })
            if (carrier.email) setValue('email', carrier.email, { shouldValidate: true })
            if (carrier.phone) setValue('phoneNumber', carrier.phone, { shouldValidate: true })
        },
        [searchMc, setValue]
    )

    // Handle DOT number change - only allow digits
    const handleDotNumberChange = useCallback(
        (value: string) => {
            const cleanValue = value.replace(/\D/g, '') // Remove non-digits
            setValue('dotNumber', cleanValue)
        },
        [setValue]
    )

    // Handle FEIN input with auto-formatting (XX-XXXXXXX)
    const handleFeinChange = useCallback(
        (value: string) => {
            let cleanValue = value.replace(/\D/g, '') // Remove non-digits
            if (cleanValue.length > 9) cleanValue = cleanValue.slice(0, 9) // Max 9 digits

            // Format as XX-XXXXXXX
            if (cleanValue.length > 2) {
                cleanValue = cleanValue.slice(0, 2) + '-' + cleanValue.slice(2)
            }

            setValue('fein', cleanValue)
        },
        [setValue]
    )

    const onSubmit = (data: CompanyInfoFormData) => {
        // Defense-in-depth: refuse to forward a known duplicate MC to the server.
        // The form-level error and disabled button are the primary block; this
        // catches any race where the user submits after a conflict was detected.
        if (blockingMcConflict) return
        // Same guard for an MC held by several carriers with no DOT picked yet.
        if (mcIsAmbiguous) return
        onNext(data)
    }

    return (
        <Box sx={{ width: '100%', mx: 'auto', height: '100%', display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '100%' }}>
                <Typography level="title-lg" sx={{ mb: 1 }}>
                    Company Information
                </Typography>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Stack spacing={1}>
                        {/* Row 1: MC Number, DOT Number */}
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            <FormField error={errors.mcNumber} sx={{ flex: '1 1 200px', minWidth: 0 }}>
                                <FormLabel>
                                    MC Number{' '}
                                    <Typography component="span" sx={{ color: 'danger.500' }}>
                                        *
                                    </Typography>
                                </FormLabel>
                                <Box sx={{ position: 'relative' }}>
                                    <Input
                                        {...register('mcNumber')}
                                        placeholder="Enter 4-9 digit MC number"
                                        endDecorator={isMcLoading ? <CircularProgress size="sm" /> : null}
                                        slotProps={{
                                            input: {
                                                inputMode: 'numeric',
                                                pattern: '[0-9]*',
                                            },
                                        }}
                                        onChange={(e) => handleMcNumberChange(e.target.value)}
                                        onBlur={() => setTimeout(() => setShowMcSuggestions(false), 200)}
                                        onFocus={() => mcSuggestions.length > 0 && setShowMcSuggestions(true)}
                                    />
                                    {/* MC Suggestions Dropdown */}
                                    {showMcSuggestions && mcSuggestions.length > 0 && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                right: 0,
                                                zIndex: 1000,
                                                bgcolor: 'background.surface',
                                                border: '1px solid',
                                                borderColor: 'neutral.outlinedBorder',
                                                borderRadius: 'sm',
                                                boxShadow: 'md',
                                                maxHeight: '200px',
                                                overflow: 'auto',
                                            }}
                                        >
                                            {mcSuggestions.map((carrier, index) => (
                                                <Box
                                                    key={index}
                                                    onClick={() => handleSelectCarrier(carrier)}
                                                    sx={{
                                                        p: 1.5,
                                                        cursor: 'pointer',
                                                        '&:hover': { bgcolor: 'primary.softBg' },
                                                        borderBottom: index < mcSuggestions.length - 1 ? '1px solid' : 'none',
                                                        borderColor: 'neutral.outlinedBorder',
                                                    }}
                                                >
                                                    <Typography level="body-sm" fontWeight="md">
                                                        {carrier.legalName}
                                                    </Typography>
                                                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                                        MC: {carrier.mcNumber} | DOT: {carrier.dotNumber}
                                                    </Typography>
                                                    {carrier.address && (
                                                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                            {[carrier.address.city, carrier.address.state].filter(Boolean).join(', ')}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            </FormField>
                            <FormField error={errors.dotNumber} sx={{ flex: '1 1 200px', minWidth: 0 }}>
                                <FormLabel>DOT Number</FormLabel>
                                <Input
                                    {...register('dotNumber')}
                                    placeholder="DOT number"
                                    slotProps={{
                                        input: {
                                            inputMode: 'numeric',
                                            pattern: '[0-9]*',
                                        },
                                    }}
                                    onChange={(e) => handleDotNumberChange(e.target.value)}
                                />
                            </FormField>
                        </Box>

                        {/* Row 2: Company Name, DBA */}
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            <FormField error={errors.companyName} sx={{ flex: '1 1 200px', minWidth: 0 }}>
                                <FormLabel>
                                    Company Name{' '}
                                    <Typography component="span" sx={{ color: 'danger.500' }}>
                                        *
                                    </Typography>
                                </FormLabel>
                                <Input {...register('companyName')} placeholder="Company name" />
                            </FormField>
                            <FormField error={errors.dba} sx={{ flex: '1 1 200px', minWidth: 0 }}>
                                <FormLabel>DBA</FormLabel>
                                <Input {...register('dba')} placeholder="Doing Business As" />
                            </FormField>
                        </Box>

                        {/* Row 3: FEIN */}
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            <FormField error={errors.fein} sx={{ flex: '1 1 200px', minWidth: 0 }}>
                                <FormLabel>FEIN</FormLabel>
                                <Input {...register('fein')} placeholder="XX-XXXXXXX" onChange={(e) => handleFeinChange(e.target.value)} />
                            </FormField>
                            <Box sx={{ flex: 1 }} />
                        </Box>

                        {/* Row 4: Address */}
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            <FormField error={errors.address1} sx={{ flex: '2 1 200px', minWidth: 0 }}>
                                <FormLabel>
                                    Address{' '}
                                    <Typography component="span" sx={{ color: 'danger.500' }}>
                                        *
                                    </Typography>
                                </FormLabel>
                                <Input {...register('address1')} placeholder="Street address" />
                            </FormField>
                            <FormField error={errors.address2} sx={{ flex: '1 1 150px', minWidth: 0 }}>
                                <FormLabel>Address 2 (Optional)</FormLabel>
                                <Input {...register('address2')} placeholder="Apt, Suite, etc." />
                            </FormField>
                        </Box>

                        {/* Row 5: City, State, ZIP */}
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            <FormField error={errors.city} sx={{ flex: '1 1 150px', minWidth: 0 }}>
                                <FormLabel>
                                    City{' '}
                                    <Typography component="span" sx={{ color: 'danger.500' }}>
                                        *
                                    </Typography>
                                </FormLabel>
                                <Input {...register('city')} placeholder="City" />
                            </FormField>
                            <FormField error={errors.state} sx={{ flex: '1 1 100px', minWidth: 0 }}>
                                <FormLabel>
                                    State{' '}
                                    <Typography component="span" sx={{ color: 'danger.500' }}>
                                        *
                                    </Typography>
                                </FormLabel>
                                <Input {...register('state')} placeholder="State" />
                            </FormField>
                            <FormField error={errors.zipCode} sx={{ flex: '1 1 100px', minWidth: 0 }}>
                                <FormLabel>
                                    ZIP Code{' '}
                                    <Typography component="span" sx={{ color: 'danger.500' }}>
                                        *
                                    </Typography>
                                </FormLabel>
                                <Input {...register('zipCode')} placeholder="ZIP" />
                            </FormField>
                        </Box>

                        {/* Row 6: Email, Phone */}
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            <FormField error={errors.email} sx={{ flex: '1 1 200px', minWidth: 0 }}>
                                <FormLabel>
                                    Email{' '}
                                    <Typography component="span" sx={{ color: 'danger.500' }}>
                                        *
                                    </Typography>
                                </FormLabel>
                                <Input {...register('email')} type="email" placeholder="company@example.com" />
                            </FormField>
                            <FormField error={errors.phoneNumber} sx={{ flex: '1 1 200px', minWidth: 0 }}>
                                <FormLabel>
                                    Phone Number{' '}
                                    <Typography component="span" sx={{ color: 'danger.500' }}>
                                        *
                                    </Typography>
                                </FormLabel>
                                <Input {...register('phoneNumber')} type="tel" placeholder="Phone number" />
                            </FormField>
                        </Box>

                        {/* Company Details */}
                        <FormField error={errors.details}>
                            <FormLabel>Company Details</FormLabel>
                            <Textarea {...register('details')} placeholder="Enter additional company details" minRows={2} />
                        </FormField>

                        <Box sx={{ mt: 1.5 }}>
                            <Button type="submit" fullWidth disabled={Boolean(blockingMcConflict) || mcIsAmbiguous}>
                                {submitLabel || 'Next'}
                            </Button>
                        </Box>
                    </Stack>
                </form>
            </Box>
        </Box>
    )
}

export default CompanyInfoStep
